import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { formatMoney } from '@/format/money'
import { AttachmentsPanel } from './AttachmentsPanel'
import {
  activateContract,
  fetchContracts,
  prolongContract,
  terminateContract,
  type Contract,
} from './documentApi'
import type { CardTab, CardTabContext } from './types'

/**
 * Договоры на карточке контрагента (тикет #73, ТЗ §4.3; вкладка карточки #70).
 *
 * ## Почему отдельная вкладка, а не блок «Финансов»
 *
 * Финансовая вкладка (#70) отвечает на вопрос «можно ли с ним работать»: лимит, долг,
 * блокировка. Договор отвечает на другой — «на каком основании и от чьего лица». Их
 * читают в разные моменты и разные люди, и общий список из лимита и срока действия
 * заставлял бы каждого пролистывать чужое.
 *
 * ## Что показывается и что нет
 *
 * `usableToday` приходит **с сервера**, а не считается здесь по датам. Это тот же ответ,
 * которым домен решает, пропустить ли заказ; повторив формулу на клиенте, мы получили бы
 * второе место, где решается, отказать ли клиенту, — и разошлись бы они молча.
 *
 * **Причины расторжения на экране нет**: колонки под неё в `crm.contract` не существует,
 * причина обязательна на входе и уходит в журнал приложения. Показывать пустое поле
 * «Причина» значило бы обещать данные, которых нет; ограничение названо надписью.
 *
 * Скан договора хранится общим механизмом вложений под родителем
 * {@code billing.contract}. До clean-вердикта ClamAV строка видна, но скачивание закрыто.
 */

/** Одна строка списка договоров плюс действия над ней. */
function ContractRow({
  contract,
  counterpartyId,
}: {
  contract: Contract
  counterpartyId: string
}) {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const [prolongTo, setProlongTo] = useState('')
  const [terminateReason, setTerminateReason] = useState('')
  const [signedOn, setSignedOn] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [attachmentsOpen, setAttachmentsOpen] = useState(false)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['contracts', counterpartyId] })
  }
  const onFailure = (error: unknown) =>
    setFailure(error instanceof ApiError ? error.message : t('app.error'))

  const activate = useMutation({
    mutationFn: () => activateContract(contract.id, signedOn, signedOn, null),
    onSuccess: () => {
      setFailure(null)
      setSignedOn('')
      invalidate()
    },
    onError: onFailure,
  })

  const prolong = useMutation({
    mutationFn: () => prolongContract(contract.id, prolongTo),
    onSuccess: () => {
      setFailure(null)
      setProlongTo('')
      invalidate()
    },
    onError: onFailure,
  })

  const terminate = useMutation({
    mutationFn: () =>
      terminateContract(contract.id, new Date().toISOString().slice(0, 10), terminateReason),
    onSuccess: () => {
      setFailure(null)
      setTerminateReason('')
      invalidate()
    },
    onError: onFailure,
  })

  // Кнопка, которая всегда получает 403, учит игнорировать ошибки — поэтому её просто нет.
  // Защита при этом на сервере: скрытый пункт — удобство, а не безопасность.
  const mayUpdate = can(user, 'billing.contract', 'update')
  const mayDelete = can(user, 'billing.contract', 'delete')

  return (
    <>
      <tr>
        <td>{contract.number}</td>
        <td>{t(`card.contract.type.${contract.contractType}`)}</td>
        <td>{t(`card.contract.status.${contract.status}`)}</td>
        <td>{contract.signedOn ?? '—'}</td>
        <td>{contract.validUntil ?? t('card.contract.openEnded')}</td>
        <td>{formatMoney(contract.amount, contract.currency, locale)}</td>
        <td>{contract.paymentTermsDays}</td>
        <td>
          {/* Пригодность считает сервер: договор, оставленный в `active` после своего
              срока, — оплошность ввода, и заказ по нему проскочить не должен. */}
          {contract.usableToday ? t('card.contract.usable') : t('card.contract.notUsable')}
        </td>
      </tr>
      <tr>
        <td colSpan={8}>
          <button
            type="button"
            aria-expanded={attachmentsOpen}
            onClick={() => setAttachmentsOpen((open) => !open)}
          >
            {attachmentsOpen
              ? t('card.contract.attachments.hide')
              : t('card.contract.attachments.show')}
          </button>
          {attachmentsOpen ? (
            <AttachmentsPanel
              entityType="billing.contract"
              entityId={contract.id}
              resource="billing.contract"
            />
          ) : null}
        </td>
      </tr>
      {(mayUpdate || mayDelete) && (
        <tr>
          <td colSpan={8}>
            {failure && (
              <p className="notice notice-error" role="alert">
                {failure}
              </p>
            )}
            <div className="card-actions">
              {mayUpdate && contract.status === 'draft' && (
                <>
                  <input
                    type="date"
                    value={signedOn}
                    aria-label={t('card.contract.signedOn')}
                    onChange={(event) => setSignedOn(event.target.value)}
                  />
                  {/* Дата подписания обязательна: договор «действует» без момента
                      возникновения обязательства — утверждение, которое нечем подкрепить. */}
                  <button
                    type="button"
                    disabled={activate.isPending || signedOn === ''}
                    onClick={() => activate.mutate()}
                  >
                    {t('card.contract.action.activate')}
                  </button>
                </>
              )}
              {mayUpdate && contract.status !== 'terminated' && contract.validUntil && (
                <>
                  <input
                    type="date"
                    value={prolongTo}
                    aria-label={t('card.contract.prolongTo')}
                    onChange={(event) => setProlongTo(event.target.value)}
                  />
                  <button
                    type="button"
                    disabled={prolong.isPending || prolongTo === ''}
                    onClick={() => prolong.mutate()}
                  >
                    {t('card.contract.action.prolong')}
                  </button>
                </>
              )}
              {mayDelete && contract.status !== 'terminated' && (
                <>
                  <input
                    type="text"
                    value={terminateReason}
                    placeholder={t('card.contract.terminateReason')}
                    aria-label={t('card.contract.terminateReason')}
                    onChange={(event) => setTerminateReason(event.target.value)}
                  />
                  {/* Причина обязательна и заглушкой не подставляется: о ней спрашивают
                      через год, и придумать её потом будет некому. */}
                  <button
                    type="button"
                    disabled={terminate.isPending || terminateReason.trim() === ''}
                    onClick={() => terminate.mutate()}
                  >
                    {t('card.contract.action.terminate')}
                  </button>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function ContractsList({ counterpartyId }: { counterpartyId: string }) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['contracts', counterpartyId],
    queryFn: ({ signal }) => fetchContracts(counterpartyId, signal),
  })

  if (query.isLoading) return <p>{t('app.loading')}</p>
  if (query.error) {
    return (
      <p role="alert">
        {query.error instanceof ApiError && query.error.isForbidden
          ? t('access.denied')
          : t('app.error')}
      </p>
    )
  }

  const contracts = query.data ?? []
  if (contracts.length === 0) {
    return <p className="muted">{t('card.contract.empty')}</p>
  }

  return (
    <div className="card-gap">
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('card.contract.number')}</th>
            <th>{t('card.contract.type')}</th>
            <th>{t('card.contract.status')}</th>
            <th>{t('card.contract.signedOn')}</th>
            <th>{t('card.contract.validUntil')}</th>
            <th>{t('card.contract.amount')}</th>
            <th>{t('card.contract.paymentTerms')}</th>
            <th>{t('card.contract.usableToday')}</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((contract) => (
            <ContractRow
              key={contract.id}
              contract={contract}
              counterpartyId={counterpartyId}
            />
          ))}
        </tbody>
      </table>
      {/* Пробел назван вслух, а не спрятан: поле «причина» без данных читалось бы как
          «расторгли без причины», а отсутствующая кнопка скачивания — как поломка. */}
      <p className="muted">{t('card.contract.gaps')}</p>
    </div>
  )
}

/** Вкладка «Договоры» карточки контрагента. */
export function contractsTab(): CardTab {
  return {
    id: 'contracts',
    labelKey: 'card.tab.contracts',
    render: (context: CardTabContext) => <ContractsList counterpartyId={context.recordId} />,
  }
}
